import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { message } from "utils/ciq-message";
import FeatureLinkingDialog from "components/feature-linking-dialog";
import FeatureItem from "components/feature-linking-dialog/feature-item";
import FeatureLinksDialogHeader from "components/feature-linking-dialog/feature-links-dialog-header";
import FeaturesGrid from "components/feature-linking-dialog/features-grid";
import DesignPackageCiqIcon from "components/svg-icons/design-package-ciq-icon";
import { ProjectContext } from "context/ProjectProvider";
import { FeatureTypes } from "entity-app/constants";
import useFeatureToFeatureLinks from "entity-app/hooks/feature-to-feature-links";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  LINK_CARD_HORIZONTAL_MARGIN_PX,
  LINKS_COLUMN_INNER_GUTTER_PX,
  LINKS_COLUMN_OUTER_PADDING_PX,
  LINKS_COLUMN_PREDECESSOR_WIDTH,
  LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
  LINKS_GROUP_TITLE_LEFT_PADDING_PX
} from "../../layout-constants";
import { PredecessorAxisLabelRow } from "../links-column/links-column-axis";
import "../design-package-successors-column/design-package-successors-column.scss";
import { sortAlphanumeric } from "utils/utils";

const LINKS_GROUP_CONTAINER_BG = "#0000000A";
const LINKS_GROUP_ACCORDION_ICON_COLOR = "#4448C1";
const LINKS_GROUP_ACCORDION_ICON_SIZE_PX = 14;
const DESIGN_PACKAGES_GROUP_ID = FeatureTypes.DESIGN_PACKAGE;

const linksGroupAccordionIconStyle: React.CSSProperties = {
  fontSize: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  width: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  height: LINKS_GROUP_ACCORDION_ICON_SIZE_PX,
  color: LINKS_GROUP_ACCORDION_ICON_COLOR
};

const linksGroupCardInsetStyle: React.CSSProperties = {
  paddingLeft: LINK_CARD_HORIZONTAL_MARGIN_PX,
  paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX
};

type TMilestone = {
  id: string;
  name: string;
  sequence_no?: number | null;
};

type TLinkedDesignPackage = {
  id: string;
  title?: string;
  number?: string;
  source_feature_milestone?: TMilestone;
  source_instance_milestone_id?: string;
  workflow_instance_milestones?: TMilestone[];
};

function buildFeatureLinkChanges(
  selectedFeatures: TLinkedDesignPackage[],
  linkedFeatures: TLinkedDesignPackage[] | undefined
) {
  const insertedLinks: TLinkedDesignPackage[] = [];
  const updatedLinks: TLinkedDesignPackage[] = [];
  const deletedLinks: TLinkedDesignPackage[] = [];
  const linkedFeaturesMap = new Map(
    linkedFeatures?.map((feature) => [feature.id, feature])
  );

  selectedFeatures.forEach((feature) => {
    const existingFeature = linkedFeaturesMap.get(feature.id);
    if (existingFeature) {
      if (
        existingFeature.source_feature_milestone?.id !==
        feature.source_feature_milestone?.id
      ) {
        insertedLinks.push(feature);
      }
      linkedFeaturesMap.delete(feature.id);
    } else {
      insertedLinks.push(feature);
    }
  });

  linkedFeaturesMap.forEach((feature) => {
    deletedLinks.push(feature);
  });

  return { insertedLinks, updatedLinks, deletedLinks };
}

function getDesignPackageMilestones(feature: TLinkedDesignPackage) {
  return (feature.workflow_instance_milestones ?? []).filter(
    (milestone): milestone is TMilestone =>
      Boolean(milestone.id && milestone.name)
  );
}

function getLinkTypeForMilestone(
  feature: TLinkedDesignPackage,
  milestoneId: string | undefined
) {
  const milestones = getDesignPackageMilestones(feature);
  const lastMilestoneId = milestones[milestones.length - 1]?.id;
  return milestoneId === lastMilestoneId ? 0 : 4;
}

function generateBidDesignLinkPayload(
  bidPackageId: string | undefined,
  changes: ReturnType<typeof buildFeatureLinkChanges>
) {
  if (!bidPackageId) return null;

  const { insertedLinks, deletedLinks } = changes;
  return {
    input: {
      deletes: deletedLinks.map((feature) => ({
        source_instance_id: feature.id,
        target_instance_id: bidPackageId
      })),
      inserts: insertedLinks.map((feature) => {
        const sourceInstanceMilestoneId = feature.source_feature_milestone?.id;
        return {
          source_instance_id: feature.id,
          source_instance_milestone_id: sourceInstanceMilestoneId,
          target_instance_id: bidPackageId,
          link_type: getLinkTypeForMilestone(
            feature,
            sourceInstanceMilestoneId
          ),
          coupling_type: 2
        };
      }),
      updates: []
    },
    metadata: {
      request_source: "DETAIL_PAGE"
    }
  };
}

function mapLinkedDesignPackages(
  linkedFeatures: any[] | null
): TLinkedDesignPackage[] {
  if (!linkedFeatures?.length) return [];

  return linkedFeatures.map((link) => ({
    id: link.source_feature_instance?.id ?? link.id,
    title: link.source_feature_instance?.title ?? link.title,
    number: link.source_feature_instance?.number ?? link.number,
    source_feature_milestone: link.source_feature_milestone,
    source_instance_milestone_id: link.source_instance_milestone_id,
    workflow_instance_milestones:
      link.source_feature_instance?.workflow_instance_milestones ??
      link.workflow_instance_milestones ??
      []
  }));
}

function getDefaultMilestone(
  feature: TLinkedDesignPackage,
  defaultMilestone?: TMilestone
) {
  const milestones = getDesignPackageMilestones(feature);
  if (!milestones.length) return undefined;

  if (defaultMilestone) {
    const matchingMilestone = milestones.find((milestone) => {
      if (
        defaultMilestone.sequence_no != null &&
        milestone.sequence_no === defaultMilestone.sequence_no
      ) {
        return true;
      }

      return milestone.name === defaultMilestone.name;
    });

    if (matchingMilestone) return matchingMilestone;
  }

  return milestones[milestones.length - 1];
}

function withDefaultMilestone(
  feature: TLinkedDesignPackage,
  defaultMilestone?: TMilestone
) {
  const milestones = getDesignPackageMilestones(feature);
  return {
    ...feature,
    source_feature_milestone:
      feature.source_feature_milestone ??
      milestones.find(
        (milestone) => milestone.id === feature.source_instance_milestone_id
      ) ??
      getDefaultMilestone(feature, defaultMilestone)
  };
}

function DesignPackagesGroupCountBadge(props: Readonly<{ count: number }>) {
  if (props.count <= 0) return null;
  return (
    <span
      className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 py-0 text-[10px] font-medium leading-4 text-ciq-gray-700"
      data-testid="links-column-group-count"
    >
      {props.count}
    </span>
  );
}

function BidPredecessorsLinkAction(
  props: Readonly<{
    children: React.ReactNode;
    onClick: () => void;
    testId: string;
  }>
) {
  return (
    <button
      type="button"
      className="design-successors-link-action"
      onClick={props.onClick}
      data-testid={props.testId}
    >
      {props.children}
    </button>
  );
}

function BidPackagePredecessorsColumn(
  props: Readonly<{
    featureId: string;
    linkedCount: number;
    linkCards: React.ReactNode;
    disabled?: boolean;
    afterChange?: () => void;
    "data-testid"?: string;
  }>
) {
  const {
    featureId,
    linkedCount,
    linkCards,
    disabled = false,
    afterChange
  } = props;
  const testId =
    props["data-testid"] ?? "entity-dateblock-new-interface-predecessors";

  const { tokenRetrievalState } = useContext(ProjectContext);
  const token = tokenRetrievalState?.token ?? "";

  const [isExpanded, setIsExpanded] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inEditMode, setInEditMode] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<
    TLinkedDesignPackage[]
  >([]);
  const [lastSelectedMilestone, setLastSelectedMilestone] = useState<
    TMilestone | undefined
  >();

  const {
    linkedFeatures,
    featuresList,
    saveLinks,
    isLoadingLinkedFeatures,
    isLoadingFeaturesList,
    isSavingLinks,
    linkedFeaturesError,
    resetFeaturesListStates
  } = useFeatureToFeatureLinks({
    token,
    featureInstanceId: featureId,
    featureTypeId: FeatureTypes.DESIGN_PACKAGE,
    linkType: "parent",
    loadLinkedFeatures: true,
    loadFeaturesList: isModalOpen
  });

  const linkedDesignPackages = useMemo(
    () => mapLinkedDesignPackages(linkedFeatures),
    [linkedFeatures]
  );

  const isLinkingInteractionDisabled =
    isSavingLinks || isLoadingLinkedFeatures || isLoadingFeaturesList;

  const openLinkingDialog = () => {
    setSelectedFeatures(
      linkedDesignPackages.map((feature) =>
        withDefaultMilestone(feature, lastSelectedMilestone)
      )
    );
    setInEditMode(false);
    setIsModalOpen(true);
  };

  const onCheckBoxAction = (data: {
    checked: boolean;
    feature: TLinkedDesignPackage;
  }) => {
    setInEditMode(true);
    if (data.checked) {
      setSelectedFeatures((prev) => [
        withDefaultMilestone(data.feature, lastSelectedMilestone),
        ...prev
      ]);
    } else {
      setSelectedFeatures((prev) =>
        prev.filter((feature) => feature.id !== data.feature.id)
      );
    }
  };

  const onUnlink = useCallback((feature: TLinkedDesignPackage) => {
    setInEditMode(true);
    setSelectedFeatures((prev) => prev.filter((f) => f.id !== feature.id));
  }, []);

  const onMilestoneChange = useCallback(
    (selectedFeatureId: string, milestone: TMilestone) => {
      setInEditMode(true);
      setLastSelectedMilestone(milestone);
      setSelectedFeatures((prev) =>
        prev.map((feature) =>
          feature.id === selectedFeatureId
            ? { ...feature, source_feature_milestone: milestone }
            : feature
        )
      );
    },
    []
  );

  const onSave = async () => {
    const linkedDesignPackagesWithMilestones = linkedDesignPackages.map(
      (feature) => withDefaultMilestone(feature)
    );
    const changes = buildFeatureLinkChanges(
      selectedFeatures,
      linkedDesignPackagesWithMilestones
    );
    const payload = generateBidDesignLinkPayload(featureId, changes);
    const response = await saveLinks(payload);

    if (response.success) {
      setIsModalOpen(false);
      resetFeaturesListStates();
      setInEditMode(false);
      message.success("Design package links updated successfully");
      afterChange?.();
    }
  };

  const linkedFeaturesDOMForDialog = useMemo(() => {
    // `selectedFeatures` is state seeded with `[]` and only ever set to arrays,
    // so `!selectedFeatures` is never true and the loading operand is dead.
    /* v8 ignore next */
    if ((!selectedFeatures && isLoadingLinkedFeatures) || linkedFeaturesError) {
      return null;
    }

    if (selectedFeatures.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-xs italic text-ciq-gray-950 opacity-80 pt-14">
          No design packages linked
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {sortAlphanumeric(selectedFeatures, "number").map((link) => (
          <FeatureItem
            key={link.id}
            number={link.number ?? ""}
            title={link.title ?? ""}
            disableUnlink={isLinkingInteractionDisabled}
            disableMilestoneSelect={isLinkingInteractionDisabled}
            onUnlink={() => onUnlink(link)}
            milestones={getDesignPackageMilestones(link)}
            selectedFeatureMilestone={link.source_feature_milestone}
            showMilestoneSelect
            milestoneSelectLabel="Linked to"
            onMilestoneChange={(milestone) =>
              onMilestoneChange(link.id, milestone)
            }
          />
        ))}
      </div>
    );
  }, [
    isLinkingInteractionDisabled,
    isLoadingLinkedFeatures,
    linkedFeaturesError,
    onUnlink,
    onMilestoneChange,
    selectedFeatures
  ]);

  const editLinksAction =
    !disabled && linkedCount > 0 ? (
      <BidPredecessorsLinkAction
        onClick={openLinkingDialog}
        testId="bid-design-packages-edit-links"
      >
        Edit Links
      </BidPredecessorsLinkAction>
    ) : null;

  return (
    <>
      <div
        className="shrink-0 flex flex-col pt-8 self-start"
        style={{ width: LINKS_COLUMN_PREDECESSOR_WIDTH }}
        data-testid={testId}
      >
        <div className="h-10 shrink-0" />
        <PredecessorAxisLabelRow
          title="Predecessors"
          count={linkedCount > 0 ? linkedCount : undefined}
        />
        <div
          className="min-w-0 py-2 space-y-2"
          style={{
            paddingLeft: LINKS_COLUMN_OUTER_PADDING_PX,
            paddingRight: LINKS_COLUMN_INNER_GUTTER_PX
          }}
        >
          <div
            className="min-w-0 overflow-hidden rounded-lg"
            style={{ backgroundColor: LINKS_GROUP_CONTAINER_BG }}
            data-testid={`links-column-group-${DESIGN_PACKAGES_GROUP_ID}`}
          >
            <div
              className="flex w-full items-center justify-between select-none"
              style={{
                paddingLeft: LINKS_GROUP_TITLE_LEFT_PADDING_PX,
                paddingRight: LINK_CARD_HORIZONTAL_MARGIN_PX,
                paddingTop: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX,
                paddingBottom: LINKS_GROUP_HEADER_VERTICAL_PADDING_PX
              }}
              data-testid="links-column-group-header"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <div className="rounded-sm flex items-center justify-center shrink-0 w-5 h-5 bg-ciq-orange-300">
                  <DesignPackageCiqIcon
                    size={10}
                    opacity="1"
                    className="stroke-ciq-orange-700"
                  />
                </div>
                <span
                  className="truncate text-xs font-semibold text-ciq-gray-800"
                  data-testid="links-column-group-label"
                >
                  Design Packages
                </span>
                <DesignPackagesGroupCountBadge count={linkedCount} />
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {editLinksAction}
                <button
                  type="button"
                  className="border-0 bg-transparent cursor-pointer p-0 leading-none"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  aria-expanded={isExpanded}
                  data-testid="links-column-group-accordion"
                >
                  {isExpanded ? (
                    <UpOutlined style={linksGroupAccordionIconStyle} />
                  ) : (
                    <DownOutlined style={linksGroupAccordionIconStyle} />
                  )}
                </button>
              </div>
            </div>
            {isExpanded ? (
              <div
                className="min-w-0 space-y-2"
                style={{
                  ...linksGroupCardInsetStyle,
                  paddingBottom: LINK_CARD_HORIZONTAL_MARGIN_PX
                }}
                data-testid="links-column-group-content"
              >
                {linkedCount > 0 ? (
                  <div
                    className="space-y-2"
                    data-testid="bid-design-packages-list"
                  >
                    {linkCards}
                  </div>
                ) : (
                  <div
                    className="flex items-center justify-between rounded border border-solid border-ciq-black-a10 bg-white px-3 py-2 text-xs"
                    data-testid="bid-design-packages-empty"
                  >
                    <span className="text-ciq-gray-800">
                      No Design Packages linked
                    </span>
                    {!disabled ? (
                      <BidPredecessorsLinkAction
                        onClick={openLinkingDialog}
                        testId="bid-design-packages-link-button"
                      >
                        Link
                      </BidPredecessorsLinkAction>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <FeatureLinkingDialog
        open={isModalOpen}
        headerComponent={
          <FeatureLinksDialogHeader
            title="Edit Links"
            onCancel={() => {
              setIsModalOpen(false);
              resetFeaturesListStates();
              setSelectedFeatures(
                linkedDesignPackages.map((feature) =>
                  withDefaultMilestone(feature, lastSelectedMilestone)
                )
              );
              setInEditMode(false);
            }}
            onSave={onSave}
            disableSaveButton={!inEditMode}
            disableCancelButton={isSavingLinks}
            saveButtonLoading={isSavingLinks}
          />
        }
        lhsComponent={
          <div className="h-full max-h-full overflow-hidden flex flex-col">
            {selectedFeatures.length > 0 ? (
              <div className="my-5 text-xs px-4">
                Linked Design Packages ({selectedFeatures.length})
              </div>
            ) : null}
            <div className="min-w-0 grow overflow-x-hidden overflow-y-auto px-4">
              {linkedFeaturesDOMForDialog}
            </div>
          </div>
        }
        rhsComponent={
          <FeaturesGrid
            featuresList={featuresList}
            onCheckBoxAction={onCheckBoxAction}
            linkedFeatures={selectedFeatures}
            isLoadingFeaturesList={isLoadingFeaturesList}
            disableCheckbox={isLinkingInteractionDisabled}
            headerLabel="List of Design Packages"
            emptyLabel="No Design Packages found"
          />
        }
      />
    </>
  );
}

export default BidPackagePredecessorsColumn;
